import { useEffect, useRef, useState } from 'react';
import { Flex, Image } from '@chakra-ui/react';

import teapotImg from 'assets/brand/teapot@2x.png';
import asBentoPage from 'common/bento/hoc/asBentoPage';
import Heading from 'components/_core/Heading';
import ProgressBar from 'components/_core/ProgressBar';
import Text from 'components/_core/Text';
import OBNavigation from 'features/Onboarding/components/OBNavigation/OBNavigation';

import locales from './locales';

// Magic numbers tweaked to have a nice loading feeling
const PROGRESS_INTERVAL_MIN = 200;
const PROGRESS_INTERVAL_MAX = 400;
const PROGRESS_MAX_TIME = 1850;
const PROGRESS_COMPLETE_IDLE = 750;

const getRandomProgressInterval = () =>
  Math.random() * (PROGRESS_INTERVAL_MAX - PROGRESS_INTERVAL_MIN) +
  PROGRESS_INTERVAL_MIN;

type CheckIdentityDocumentProps = {
  onContinue: () => void;
};

/**
 * This page is a fake loading screen we want to display when we already know that
 * the VID was invalidated by the identity provider and should be fixed before submission.
 * The loader is here for a UX purpose to make the transition to the identity module less
 * suprising and abrupt after trying to submit.
 */
const CheckIdentityDocument = asBentoPage<CheckIdentityDocumentProps>(
  ({ Layout, onContinue }) => {
    const startTime = useRef(Date.now());
    const [progress, setProgress] = useState(0);

    useEffect(() => {
      if (progress < 1) {
        setTimeout(() => {
          const ratio = (Date.now() - startTime.current) / PROGRESS_MAX_TIME;
          setProgress(Math.min(ratio, 1));
        }, getRandomProgressInterval());
      } else {
        setTimeout(onContinue, PROGRESS_COMPLETE_IDLE);
      }
    }, [onContinue, progress, setProgress]);

    return (
      <Layout nav={<OBNavigation />}>
        <Flex direction="column" height={{ base: 'full', md: 'auto' }}>
          <Image
            height="180px"
            marginBottom="space-16"
            src={teapotImg}
            width="180px"
          />

          <Heading marginBottom="space-32" size="lg">
            {locales.title}
          </Heading>

          <ProgressBar
            height="4px"
            marginBottom="space-16"
            maxWidth="65%"
            ratio={progress}
          />

          <Text variant="light">{locales.progressHint}</Text>
        </Flex>
      </Layout>
    );
  },
);

export default CheckIdentityDocument;
